import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../lib/api';
import { dict, useT } from '../lib/lang';
import { textOf } from '../lib/format';
import { Icon } from './icons';

/**
 * Bộ phận dùng chung.
 *
 * Gom vào một file thay vì mỗi thứ một file: panel dùng đúng chừng này khối, và
 * tách ra 12 file 20 dòng chỉ làm việc tìm chúng khó hơn.
 *
 * ── Mọi màu ở đây đi qua token (14/08/2026) ────────────────────────────────
 *
 * Không còn một cặp sáng/tối viết tay nào (kiểu `bg-white` cộng `dark:bg-...`)
 * trong file này. Bảng token nằm ở `src/index.css`; nếu cần một sắc độ chưa có
 * tên, thêm token ở đó chứ
 * đừng viết thẳng một class màu vào đây — chỗ này là nơi 60 trang đi qua, nên
 * một màu viết cứng ở đây là một màu không đổi được ở 60 trang.
 *
 * Nguyên tắc hình ảnh của bản này: NỔI BẰNG LỚP NỀN, không bằng đổ bóng.
 * `shadow-card` ở chế độ tối là `none` — thẻ tách khỏi nền chỉ nhờ chênh lệch
 * `--surface` với `--app`. Chỗ duy nhất còn bóng thật là `StickyActions`, và có
 * lý do riêng ghi ngay tại đó.
 */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ── Khung nội dung ──────────────────────────────────────────────────────────

export function Page({
  title,
  icon,
  description,
  actions,
  children,
}: {
  title: string;
  /**
   * Hình đứng trước tiêu đề.
   *
   * Có mặt vì `pages/Category.tsx` từng dựng tiêu đề bằng cách NỐI CHUỖI
   * (`${category.icon} ${name}`). Với emoji thì nối được; với một hình SVG thì
   * không, và cũng không nên có cách nào để nối — một hình vẽ không phải một
   * ký tự trong câu.
   */
  icon?: ReactNode;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-2xl font-semibold tracking-heading text-fg">
            {icon !== undefined && (
              <span className="shrink-0 text-fg-subtle" aria-hidden>
                {icon}
              </span>
            )}
            <span className="min-w-0">{title}</span>
          </h1>
          {description !== undefined && (
            <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-fg-muted">{description}</p>
          )}
        </div>
        {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

/**
 * Nhãn của một cụm — nhỏ, chữ hoa, giãn chữ.
 *
 * Dành cho một nhóm khối KHÔNG phải là `Card`: `Card` mang theo cả viền và nền,
 * và bọc một lưới thẻ số vào trong nó là dựng hai lớp khung lồng nhau. Thứ cần
 * ở đó chỉ là một dòng nói cụm này nói về cái gì.
 */
export function SectionHeading({
  title,
  hint,
  actions,
}: {
  title: string;
  hint?: string;
  actions?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <h2 className="text-[0.68rem] font-medium uppercase tracking-label text-fg-subtle">
        {title}
      </h2>
      {hint !== undefined && <p className="text-xs text-fg-subtle">{hint}</p>}
      {actions !== undefined && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Lưới thẻ dẫn đường — mỗi thẻ là một `<Link>` kèm một câu nói nó dẫn tới đâu.
 *
 * Hai chỗ gọi, và chúng là hai tầng của cùng một bản đồ: trang category (thẻ =
 * một trang) và cuối trang Tổng quan (thẻ = một category). Dùng chung một khối
 * để hai tầng trông cùng một hệ — người dùng học cách đọc nó một lần.
 *
 * `icon` nhận `ReactNode` chứ không còn là `string`: từ lượt bỏ emoji, thứ
 * truyền vào là một `<Icon>`.
 */
export function LinkCardGrid({
  items,
}: {
  items: readonly {
    readonly to: string;
    readonly title: string;
    readonly description: string;
    readonly icon?: ReactNode;
    /** Nhãn nhỏ góc phải — dùng cho dấu của trang cấu hình. */
    readonly tag?: ReactNode;
  }[];
}): React.JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="group rounded-xl border border-border bg-surface p-4 transition-colors hover:border-accent hover:bg-accent-soft"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2.5">
              {item.icon !== undefined && (
                <span
                  className="shrink-0 text-fg-subtle transition-colors group-hover:text-accent"
                  aria-hidden
                >
                  {item.icon}
                </span>
              )}
              <span className="font-medium text-fg">{item.title}</span>
            </span>
            {item.tag !== undefined && (
              <span className="flex shrink-0 items-center gap-1 text-[0.65rem] uppercase tracking-label text-fg-subtle">
                {item.tag}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{item.description}</p>
        </Link>
      ))}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  className,
  children,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section
      className={cx('rounded-xl border border-border bg-surface shadow-card', className)}
    >
      {(title !== undefined || actions !== undefined) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {title !== undefined && (
              <h2 className="text-sm font-medium tracking-heading text-fg">{title}</h2>
            )}
            {subtitle !== undefined && (
              <p className="mt-0.5 text-xs leading-relaxed text-fg-subtle">{subtitle}</p>
            )}
          </div>
          {actions !== undefined && <div className="flex gap-2">{actions}</div>}
        </div>
      )}
      {/* `p-4` là con số mà `Table` bám vào bằng `-mx-4` để tràn hết bề ngang —
          xem chú thích ở `Table`. Đổi nó ở đây là làm lệch mọi bảng trong panel. */}
      <div className="p-4">{children}</div>
    </section>
  );
}

// ── Thẻ chỉ số ──────────────────────────────────────────────────────────────

export interface StatProps {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: 'neutral' | 'good' | 'warn' | 'bad';
  /** Dòng so sánh kỳ trước, ví dụ "15% so với 30 ngày trước đó". */
  readonly delta?: string;
  /**
   * Màu của dòng so sánh — do CHỖ GỌI quyết, không suy từ `deltaDirection`.
   *
   * Mũi tên đi lên KHÔNG mặc định là tin tốt: số lỗ hổng và số ticket tăng lên
   * là tin xấu. Một thẻ tự tô xanh cho mọi mũi tên đi lên là một thẻ biết nói
   * dối ở mọi trang dùng nó, nên nó không được phép biết luật đó.
   */
  readonly deltaTone?: 'good' | 'bad' | 'neutral';
  readonly deltaDirection?: 'up' | 'down';
  /** Số không lấy được: hiện “—”, và `hint` là chỗ nói vì sao. */
  readonly unavailable?: boolean;
  /** Chú thích khi rê chuột — dành cho con số gộp từ nhiều nguồn. */
  readonly title?: string;
  /** Cỡ số to hơn. Chỉ dành cho hàng chỉ số dẫn đầu của một trang. */
  readonly emphasis?: boolean;
}

const STAT_TONE: Record<NonNullable<StatProps['tone']>, string> = {
  neutral: 'text-fg',
  good: 'text-good',
  warn: 'text-warn',
  bad: 'text-bad',
};

const DELTA_TONE: Record<NonNullable<StatProps['deltaTone']>, string> = {
  good: 'text-good',
  bad: 'text-bad',
  neutral: 'text-fg-subtle',
};

/**
 * Ruột của một ô chỉ số, KHÔNG kèm viền hay nền.
 *
 * Tách ra vì hai vỏ dùng chung nó: `StatCard` (một thẻ đứng riêng) và
 * `StatGroup` (một ô trong bảng chỉ số liền khối).
 *
 * `font-mono` + `tabular-nums` cho con số là thay đổi đáng kể nhất của cả lượt
 * sửa giao diện: chữ số đều bề ngang thì cột số thẳng hàng khi số đổi, và một
 * con số thẳng hàng đọc ra như số đo của thiết bị chứ không như một từ trong
 * đoạn văn.
 */
function StatBody({
  label,
  value,
  hint,
  tone = 'neutral',
  delta,
  deltaTone = 'neutral',
  deltaDirection,
  unavailable = false,
  emphasis = false,
}: StatProps): React.JSX.Element {
  return (
    <>
      {/*
        Nhãn chiếm CHỖ của hai dòng dù chỉ viết một.

        Nhãn tiếng Anh dài hơn tiếng Việt và hay xuống dòng ("Questions the bot
        answered itself"), nên trong một hàng bốn ô sẽ có ô nhãn một dòng, ô nhãn
        hai dòng — và các con số tụt xuống ở những cột khác nhau. Cả hàng mất
        đường chuẩn, đúng thứ mà một hàng chỉ số sinh ra để có.
      */}
      <p className="flex min-h-[2.4em] items-start text-[0.68rem] font-medium uppercase leading-[1.2] tracking-label text-fg-subtle">
        {label}
      </p>
      <p
        className={cx(
          'mt-1.5 font-mono font-semibold tabular-nums tracking-heading',
          emphasis ? 'text-3xl' : 'text-2xl',
          unavailable ? 'text-fg-subtle' : STAT_TONE[tone],
        )}
      >
        {unavailable ? '—' : value}
      </p>
      {delta !== undefined && !unavailable && (
        <p
          className={cx(
            'mt-1.5 inline-flex items-center gap-1 font-mono text-xs tabular-nums',
            DELTA_TONE[deltaTone],
          )}
        >
          {deltaDirection !== undefined && (
            <Icon name={deltaDirection === 'up' ? 'trend-up' : 'trend-down'} size={12} />
          )}
          {delta}
        </p>
      )}
      {hint !== undefined && <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{hint}</p>}
    </>
  );
}

export function StatCard(props: StatProps): React.JSX.Element {
  return (
    <div
      className="rounded-xl border border-border bg-surface p-4 shadow-card"
      title={props.title}
    >
      <StatBody {...props} />
    </div>
  );
}

/**
 * Nhiều chỉ số trong MỘT khối, chia ô bằng viền tóc.
 *
 * ── Vì sao tồn tại ────────────────────────────────────────────────────────
 *
 * Trang Tổng quan từng bày mười một `StatCard` rời nhau trong hai lưới. Mười
 * một cái hộp có viền, có nền, có bóng, cùng kích cỡ, xếp cạnh nhau — mắt không
 * có chỗ nào để đáp, và cả trang đọc ra như một bảng điều khiển của máy công
 * nghiệp. Vấn đề không nằm ở số lượng con số mà ở số lượng VẬT THỂ: mỗi thẻ tự
 * khẳng định nó là một đơn vị độc lập, trong khi bốn con số cạnh nhau là MỘT
 * câu chuyện.
 *
 * Nên nhóm chỉ số giờ là một tấm bảng duy nhất, các ô ngăn nhau bằng đúng một
 * đường 1px. Số vật thể giảm từ mười một xuống ba, mà không mất một con số nào.
 *
 * Ở màn hình hẹp lưới đổ về một cột, và đường ngăn cũng đổi từ dọc sang ngang —
 * đó là lý do lớp lưới và lớp `divide-*` phải đổi tại CÙNG một điểm ngắt, và
 * cũng là lý do bảng tra dưới đây gộp cả hai vào một chuỗi.
 */
const STAT_LAYOUT: Record<2 | 3 | 4, string> = {
  2: 'sm:grid-cols-2 sm:divide-x sm:divide-y-0',
  3: 'md:grid-cols-3 md:divide-x md:divide-y-0',
  4: 'md:grid-cols-4 md:divide-x md:divide-y-0',
};

export function StatGroup({
  items,
  columns = 3,
  className,
}: {
  items: readonly StatProps[];
  columns?: 2 | 3 | 4;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cx(
        'grid divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface shadow-card',
        STAT_LAYOUT[columns],
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="p-4 sm:p-5" title={item.title}>
          <StatBody {...item} />
        </div>
      ))}
    </div>
  );
}

/**
 * Dãy nút chọn một-trong-nhiều.
 *
 * Dùng cho hai việc trông khác nhau nhưng cùng một hình: lọc một danh sách, và
 * chuyển giữa các mảng của một trang dài. Cả hai đều là "chọn một", và cả hai
 * đều hỏng theo cùng một kiểu khi làm bằng `<Select>` — ô chọn giấu mất các lựa
 * chọn còn lại, nên người dùng không biết trang có gì cho tới khi bấm mở ra.
 *
 * `variant` nói ra hai việc đó thay vì ép một hình cho cả hai:
 *
 *  • `chip` — LỌC. Các lựa chọn ngang hàng nhau, không cái nào là "mục lục".
 *  • `rail` — MỤC của một trang dài. Gạch chân dưới một đường kẻ liền, đúng hình
 *    mà mọi người đã học từ hệ thống tab của trình duyệt.
 *
 * Trạng thái đang chọn của `chip` KHÔNG còn là một viên chàm tô đặc: một mảng
 * màu đặc giữa một trang toàn số liệu kéo mắt về phía một bộ lọc, trong khi thứ
 * đáng được nhìn là các con số.
 *
 * `count` hiện cả khi bằng 0: "không có mục nào" cũng là một câu trả lời, và
 * giấu nó đi khiến người dùng tưởng bộ lọc bị hỏng.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  className,
  variant = 'chip',
}: {
  value: T;
  options: readonly { value: T; label: string; count?: number; hint?: string }[];
  onChange: (next: T) => void;
  className?: string;
  variant?: 'chip' | 'rail';
}): React.JSX.Element {
  const rail = variant === 'rail';

  return (
    <div
      className={cx(
        rail
          ? 'flex flex-wrap items-center gap-1 border-b border-border'
          : 'flex flex-wrap items-center gap-1.5',
        className,
      )}
      role="tablist"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            title={option.hint}
            onClick={() => onChange(option.value)}
            className={cx(
              'transition-colors',
              rail
                ? cx(
                    '-mb-px border-b-2 px-3 py-2 text-xs font-medium',
                    active
                      ? 'border-accent text-fg'
                      : 'border-transparent text-fg-muted hover:border-border-strong hover:text-fg',
                  )
                : cx(
                    'rounded-lg border px-2.5 py-1 text-xs font-medium',
                    active
                      ? 'border-accent-edge bg-accent-soft text-accent-soft-fg'
                      : 'border-border bg-surface-2 text-fg-muted hover:bg-surface-3 hover:text-fg',
                  ),
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span className="ml-1 font-mono tabular-nums opacity-70">({option.count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Khối gập lại được, đóng sẵn.
 *
 * Dành cho phần dữ liệu ĐÚNG nhưng không phải thứ người mở trang đang tìm — một
 * bảng 90 dòng nằm mở sẵn không làm ai đọc nó kỹ hơn, nó chỉ đẩy mọi khối phía
 * dưới ra khỏi tầm nhìn. Dùng `<details>` gốc chứ không tự dựng bằng state: nó
 * mở được bằng bàn phím, và Ctrl+F của trình duyệt tìm thấy nội dung bên trong.
 */
export function Disclosure({
  summary,
  hint,
  defaultOpen = false,
  children,
}: {
  summary: string;
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <details open={defaultOpen} className="group rounded-lg border border-border">
      <summary className="flex cursor-pointer select-none items-center gap-2 px-4 py-2.5 text-sm font-medium text-fg hover:bg-surface-2">
        <span
          aria-hidden="true"
          className="shrink-0 text-fg-subtle transition-transform group-open:rotate-90"
        >
          <Icon name="caret-right" size={12} />
        </span>
        {summary}
        {hint !== undefined && <span className="text-xs font-normal text-fg-subtle">{hint}</span>}
      </summary>
      {/* `p-4` chứ không phải `p-3`: `Table` dùng `-mx-4` để tràn hết bề ngang khối
          chứa nó, và nó được tính theo đúng khoảng đệm này của `Card`. */}
      <div className="border-t border-border p-4">{children}</div>
    </details>
  );
}

// ── Nút và ô nhập ───────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

/*
 * `ring-offset-app` chứ không phải một màu trắng viết cứng.
 *
 * Vòng focus vẽ cách nút một khoảng, và khoảng đó phải mang màu NỀN TRANG. Viết
 * cứng màu trắng thì trên nền `#020617` nó thành một vành sáng quanh mọi nút vừa
 * được bấm bằng bàn phím — vừa xấu vừa nói sai về hình dạng của nút.
 */
const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-app';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-fg-onaccent hover:bg-accent-hover',
  secondary:
    'border border-border-strong bg-surface-2 text-fg hover:border-accent hover:bg-surface-3',
  danger: 'bg-danger text-white hover:bg-danger-hover',
  ghost: 'text-fg-muted hover:bg-surface-2 hover:text-fg',
};

/**
 * Cùng bộ class, cho thứ KHÔNG phải `<button>`.
 *
 * Một `<Link>` trông như nút vẫn phải là `<a>` — nó điều hướng, nên nó cần mở
 * được ở tab mới và cần trình đọc màn hình đọc ra là liên kết. Trước đây chỗ duy
 * nhất cần điều đó đã chép tay cả chuỗi class của `secondary`, và một bản chép
 * tay là một bản sẽ lệch ở lần đổi màu tiếp theo mà không ai để ý.
 */
export function buttonClass(variant: ButtonVariant = 'secondary', className?: string): string {
  return cx(BUTTON_BASE, BUTTON_VARIANTS[variant], className);
}

export function Button({
  variant = 'secondary',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }): React.JSX.Element {
  return <button type="button" className={buttonClass(variant, className)} {...props} />;
}

/**
 * Nút gạt bật/tắt (15/08/2026) — thay `<input type="checkbox">` cho MỌI công
 * tắc bật/tắt MỘT tính năng. Không thay cho checkbox chọn-nhiều-từ-danh-sách
 * (chọn vai trò, chọn kênh, tick hàng loạt) — hai việc khác nhau, xem
 * `pages/PanelAccess.tsx`/`pages/RewardsQueue.tsx` cho ví dụ vẫn giữ checkbox.
 *
 * Dựng bằng `<button role="switch">`, không phải `<label><input></label>`: gộp
 * cả ô gạt lẫn chữ nhãn vào MỘT phần tử bấm được — bấm chữ hay bấm ô gạt đều có
 * tác dụng, và Enter/Space hoạt động miễn phí nhờ là `<button>`. Trước bản này
 * có NĂM bản sao gần như giống hệt nhau của một khối "checkbox + nhãn" rải rác
 * ở `messageForm.tsx` và bốn trang khác nhau — dồn về đây, xoá bốn bản sao.
 */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  /** Dòng phụ dưới nhãn, cùng dáng với `hint` của `Field`. */
  hint?: ReactNode;
  disabled?: boolean;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'inline-flex items-start gap-2.5 rounded-md text-sm font-medium text-fg disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-app',
        className,
      )}
    >
      <span
        aria-hidden
        className={cx(
          'relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
          checked ? 'border-good bg-good' : 'border-border-strong bg-surface-3',
        )}
      >
        <span
          className={cx(
            'inline-block h-4 w-4 translate-x-1 rounded-full bg-white shadow-sm transition-transform',
            checked && 'translate-x-[22px]',
          )}
        />
      </span>
      {label !== undefined && (
        <span className="text-left">
          {label}
          {hint !== undefined && <span className="block text-xs font-normal text-fg-subtle">{hint}</span>}
        </span>
      )}
    </button>
  );
}

/*
 * Ô nhập là một GIẾNG LÕM, không phải một khối nổi.
 *
 * `bg-field` ở chế độ tối tối hơn cả thẻ chứa nó, và `shadow-sm` biến mất — một
 * cái hộp nổi lên khỏi mặt thẻ trông như một cái nút, trong khi đây là chỗ để
 * gõ chữ vào. Đây là thay đổi làm nhiều cho cảm giác "công nghệ cao" hơn mọi
 * lượt tinh chỉnh viền cộng lại.
 */
const fieldClass =
  'rounded-lg border border-border-strong bg-field px-3 py-1.5 text-sm text-fg ' +
  'placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent ' +
  'disabled:bg-surface-2 disabled:text-fg-subtle';

/**
 * `w-full` mặc định, và CHỈ khi chỗ gọi chưa tự khai bề rộng.
 *
 * Trước đây `w-full` nằm cứng trong `fieldClass` và `props.className` được nối
 * vào SAU, nên `<Select className="w-40">` trông như phải thắng. Nó không thắng:
 * CSS không quan tâm thứ tự chữ trong thuộc tính `class`, nó lấy quy tắc đứng sau
 * trong file — và Tailwind xếp `.w-full` sau `.w-40`. Kết quả là mọi ô lọc, ô
 * chọn phạm vi, ô tìm kiếm trong panel đều âm thầm giãn hết hàng, dựng bộ lọc
 * ba ô nằm ngang thành ba dòng chồng lên nhau. Không thấy được từ code, chỉ thấy
 * khi mở trang ra nhìn.
 *
 * Nên bề rộng được quyết ở TẦNG TSX, nơi thứ tự có thật, thay vì phó cho thứ tự
 * xuất hiện trong file CSS sinh tự động.
 *
 * `max-w-*` và `min-w-*` KHÔNG tính là khai bề rộng — chúng là trần và sàn, và
 * một ô chỉ có `max-w-xs` mà mất `w-full` sẽ co lại về bề rộng mặc định của thẻ
 * `<input>` thay vì giãn ra rồi dừng ở 20rem.
 *
 * Lượt dựng token (14/08/2026) giữ nguyên cơ chế này có chủ ý: `fieldClass` đổi
 * màu, còn cách quyết bề rộng thì không đụng tới. Đừng "dọn" nó bằng cách nhét
 * `w-full` trở lại vào chuỗi class.
 */
const HAS_WIDTH = /(^|\s)(w-\S|flex-1|grow)/;

function fieldWidth(className: string | undefined): string {
  return className !== undefined && HAS_WIDTH.test(className) ? '' : 'w-full';
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return <input {...props} className={cx(fieldClass, fieldWidth(props.className), props.className)} />;
}

export function Textarea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
): React.JSX.Element {
  return (
    <textarea
      {...props}
      className={cx(fieldClass, fieldWidth(props.className), 'font-mono', props.className)}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  return <select {...props} className={cx(fieldClass, fieldWidth(props.className), props.className)} />;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-fg">{label}</span>
      {hint !== undefined && (
        <span className="block text-xs leading-relaxed text-fg-subtle">{hint}</span>
      )}
      {children}
      {error !== undefined && error.length > 0 && (
        <span className="block text-xs text-bad">{error}</span>
      )}
    </label>
  );
}

/**
 * Ô nhập dạng thẻ, cho keywords và variants.
 *
 * Enter hoặc dấu phẩy để chốt một thẻ. Backspace ở ô rỗng xoá thẻ cuối — thói
 * quen từ mọi ô nhập thẻ khác, và không có nó thì phải rê chuột đi bấm chữ ×.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  suggestions,
  suggestionsId,
}: {
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /**
   * Gợi ý cho ô gõ — hiện qua `<datalist>` gốc của trình duyệt.
   *
   * Là GỢI Ý chứ không phải danh sách đóng, và đó là chủ đích: nguồn của nó
   * (danh sách model do nhà cung cấp trả về) có thể thiếu — endpoint tự host
   * không cài `/models`, hoặc trả danh mục của hãng trong khi khoá này chỉ dùng
   * được vài model. Biến nó thành ô chọn đóng là để một danh sách không đầy đủ
   * chặn người vận hành khai thứ họ biết là có thật.
   */
  suggestions?: readonly string[];
  /** Bắt buộc khi có `suggestions` — hai ô trên cùng trang không được trùng id. */
  suggestionsId?: string;
}): React.JSX.Element {
  const t = useT(UI_TEXT);
  const [draft, setDraft] = useState('');
  // Bỏ những cái đã chọn: gợi ý lại một thẻ đang nằm ngay trên ô là nhiễu, và
  // `commit` cũng đã chặn trùng nên chọn nó không sinh ra gì.
  const unused = (suggestions ?? []).filter(
    (item) => !value.some((tag) => tag.toLowerCase() === item.toLowerCase()),
  );
  const listId = suggestionsId !== undefined && unused.length > 0 ? suggestionsId : undefined;

  const commit = (raw: string): void => {
    const trimmed = raw.trim().replace(/,$/, '').trim();
    if (trimmed.length === 0) return;
    if (value.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  return (
    <div
      className={cx(
        fieldClass,
        'flex min-h-[2.4rem] w-full flex-wrap items-center gap-1.5 py-1.5',
      )}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded border border-accent-edge bg-accent-soft px-2 py-0.5 text-xs text-accent-soft-fg"
        >
          {tag}
          <button
            type="button"
            aria-label={t.remove(tag)}
            className="text-accent-soft-fg opacity-70 transition-opacity hover:text-bad hover:opacity-100"
            onClick={() => onChange(value.filter((item) => item !== tag))}
          >
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      {listId !== undefined && (
        <datalist id={listId}>
          {unused.map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
      )}
      <input
        value={draft}
        list={listId}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-w-[8rem] flex-1 bg-transparent text-sm focus:outline-none"
        onChange={(event) => {
          const next = event.target.value;
          if (next.endsWith(',')) commit(next);
          // Chọn một mục từ datalist thì trình duyệt điền TRỌN giá trị vào ô mà
          // không sinh sự kiện bàn phím nào — không bắt ở đây thì người dùng
          // bấm chọn xong vẫn phải tự gõ Enter, và phần lớn sẽ không nghĩ ra.
          else if (unused.includes(next)) commit(next);
          else setDraft(next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(draft);
          } else if (event.key === 'Backspace' && draft.length === 0 && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}

// ── Trạng thái ──────────────────────────────────────────────────────────────

/**
 * Nhãn trạng thái.
 *
 * Có VIỀN, không chỉ có nền. Một viên thuốc chỉ tô nền nhạt là hình phổ biến
 * nhất của các bảng quản trị dựng vội; thêm một đường viền cùng tông làm nó
 * thành một vật có hình dạng, và ở chế độ tối thì viền còn là thứ duy nhất giữ
 * cho nhãn không tan vào nền thẻ.
 */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
  children: ReactNode;
}): React.JSX.Element {
  const tones = {
    neutral: 'border-border bg-surface-2 text-fg-muted',
    good: 'border-good-edge bg-good-soft text-good-soft-fg',
    warn: 'border-warn-edge bg-warn-soft text-warn-soft-fg',
    bad: 'border-bad-edge bg-bad-soft text-bad-soft-fg',
    // `info` cố ý dùng chung họ màu với accent: nhãn thông tin và điểm nhấn của
    // panel vốn đã cùng màu chàm, nên tách chúng ra là mời hai thứ lệch nhau.
    info: 'border-accent-edge bg-accent-soft text-accent-soft-fg',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[0.7rem] font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Chuỗi của các thành phần dùng chung.
 *
 * Chúng xuất hiện ở gần như mọi trang, nên để chúng ở đây thay vì bắt từng
 * trang tự truyền vào — một nút "Huỷ" dịch khác nhau ở ba mươi chỗ là ba mươi
 * cơ hội để lệch.
 */
const UI_TEXT = dict({
  en: {
    loading: 'Loading…',
    genericError: 'Something went wrong.',
    confirm: 'Confirm',
    cancel: 'Cancel',
    remove: (tag: string) => `Remove ${tag}`,
    dismiss: 'Dismiss this warning',
  },
  vi: {
    loading: 'Đang tải…',
    genericError: 'Đã có lỗi xảy ra.',
    confirm: 'Xác nhận',
    cancel: 'Huỷ',
    remove: (tag: string) => `Xoá ${tag}`,
    dismiss: 'Đóng cảnh báo này',
  },
});

export function Spinner({ label }: { label?: string }): React.JSX.Element {
  const t = useT(UI_TEXT);
  const text = label ?? t.loading;
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-fg-subtle">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-accent" />
      {text}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="py-8 text-center text-sm text-fg-subtle">{children}</p>;
}

/**
 * Thanh hành động BÁM ĐÁY màn hình, mang theo báo lỗi của chính nó.
 *
 * ── Vì sao tồn tại ────────────────────────────────────────────────────────
 *
 * Hai lỗi cùng một gốc, cả hai đều đã xảy ra thật trên panel này:
 *
 *  1. **Nút phải đi tìm là nút bị quên.** Trang cấu hình dài hơn trăm ô, nút Lưu
 *     ở đầu trang — mọi lượt chỉnh đều kết thúc bằng việc cuộn ngược lên, và
 *     người ta đổi vài ô rồi rời trang, tin rằng mình đã lưu.
 *  2. **Lỗi ở đầu trang, nút ở cuối trang.** Bấm Lưu, máy chủ từ chối, màn hình
 *     không đổi gì cả. Kết luận hợp lý nhất của người dùng — "chắc lưu xong rồi"
 *     — là kết luận sai, và không có gì trên màn hình cải chính nó.
 *
 * Nên nút và lỗi của nó phải ĐI CÙNG NHAU, ở đúng chỗ con mắt vừa nhìn.
 *
 * ── Vì sao `sticky` chứ không `fixed` ─────────────────────────────────────
 *
 * `fixed` che mất ô cuối cùng của biểu mẫu ở đúng lúc người dùng đang điền nó.
 * `sticky bottom-4` ghim vào đáy khung nhìn suốt lúc trang còn cuộn được, rồi tự
 * về đúng chỗ khi cuộn tới cuối — với điều kiện nó là con TRỰC TIẾP của khung
 * trang, không bị bọc trong một thẻ có `overflow` riêng. Cụ thể: đừng thêm
 * `overflow-*` vào cột nội dung ở `components/Layout.tsx`, kể cả để "chữa" một
 * thanh cuộn thừa — nó sẽ làm thanh này ngừng bám ở cả bảy trang dùng nó, và
 * ngừng một cách hoàn toàn im lặng.
 */
export function StickyActions({
  error,
  children,
}: {
  /**
   * Lỗi của chính những nút trong thanh này. Nhận mảng để một thanh gom được
   * nhiều mutation (lưu, xoá, khôi phục) mà chỗ gọi không phải tự dựng khung.
   */
  error?: unknown;
  children: ReactNode;
}): React.JSX.Element {
  // Khai kiểu tường minh: `Array.isArray` trên một `unknown` thu hẹp về `any[]`,
  // và `any` chảy tiếp xuống `ErrorBox` thì mất luôn phần kiểm kiểu ở đó.
  const list: readonly unknown[] = Array.isArray(error) ? (error as readonly unknown[]) : [error];
  const errors = list.filter((item) => item !== null && item !== undefined);

  return (
    <div className="sticky bottom-4 z-20 space-y-2">
      {errors.length > 0 && (
        /*
         * Nền ĐỤC bọc ngoài `ErrorBox`.
         *
         * Nền của `ErrorBox` trong suốt một phần. Không sao khi nó nằm yên trong
         * luồng trang, nhưng ghim đè lên nội dung đang cuộn thì chữ của biểu mẫu
         * chạy xuyên qua và câu báo lỗi thành thứ không đọc nổi. Bọc một lớp nền
         * rẻ hơn sửa `ErrorBox` — sửa nó sẽ đổi mọi trang khác đang dùng nó đúng
         * cách.
         *
         * `max-h` + cuộn riêng: một lỗi validate có thể liệt kê hàng chục trường,
         * và một hộp lỗi cao bằng màn hình thì che mất chính cái nút cần bấm.
         */
        <div className="max-h-52 space-y-2 overflow-y-auto rounded-lg bg-surface/95 backdrop-blur">
          {errors.map((item, index) => (
            <ErrorBox key={index} error={item} />
          ))}
        </div>
      )}
      {/*
        `shadow-lg` là CHỖ DUY NHẤT trong panel còn giữ một cái bóng thật, kể cả
        ở chế độ tối nơi `shadow-card` đã thành `none`. Nó không phải trang trí
        còn sót: thanh này TRÔI trên nội dung đang cuộn bên dưới, và cái bóng là
        thứ nói ra điều đó. Đừng dọn nó đi cho "nhất quán với các thẻ khác" —
        các thẻ khác không trôi.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent bg-surface/95 px-4 py-3 shadow-lg backdrop-blur">
        {children}
      </div>
    </div>
  );
}

/**
 * Hiển thị lỗi từ API.
 *
 * Luôn in cả `details`: thông điệp chính thường chỉ nói "dữ liệu không hợp lệ",
 * còn thứ người dùng cần biết — trường nào sai — nằm ở danh sách chi tiết.
 */
export function ErrorBox({ error }: { error: unknown }): React.JSX.Element | null {
  const t = useT(UI_TEXT);
  if (error === null || error === undefined) return null;

  const message = error instanceof ApiError ? error.message : textOf(error, t.genericError);
  const details = error instanceof ApiError ? error.details : [];

  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-bad-edge bg-bad-soft p-3 text-sm text-bad-soft-fg">
      <span className="mt-0.5 shrink-0 text-bad" aria-hidden>
        <Icon name="prohibit" size={16} />
      </span>
      <div className="min-w-0">
        <p className="font-medium">{message}</p>
        {details.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-xs">
            {details.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Cảnh báo Ở LẠI trên màn hình cho tới khi người dùng tự đóng.
 *
 * ── Vì sao KHÔNG dùng `Toast` cho việc này ────────────────────────────────
 *
 * `Toast` tự biến mất sau 4 giây. Đó là kênh đúng cho "đã lưu xong", và là kênh
 * SAI cho "đã lưu nhưng có vấn đề". Chuyện đã xảy ra thật: lượt thêm khoá AI trả
 * về cảnh báo "đã lưu nhưng chưa kiểm chứng được khoá", cảnh báo đó bị nối vào
 * cuối câu chúc mừng, loé lên bốn giây rồi mất — người vận hành nhớ là "thêm
 * khoá thành công", trong khi log của bot ghi khoá hỏng. Hai nguồn sự thật nói
 * ngược nhau, và nguồn sai là nguồn họ nhìn thấy.
 *
 * Cảnh báo là thứ CẦN ĐỌC và cần làm gì đó, nên nó phải nằm yên cho tới khi
 * người ta chủ động đóng.
 */
export function WarningBox({
  message,
  onDismiss,
}: {
  message: string | null;
  onDismiss?: () => void;
}): React.JSX.Element | null {
  const t = useT(UI_TEXT);
  if (message === null || message.length === 0) return null;

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-warn-edge bg-warn-soft p-3 text-sm text-warn-soft-fg">
      <p className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0 text-warn" aria-hidden>
          <Icon name="warning" size={16} />
        </span>
        <span className="min-w-0">{message}</span>
      </p>
      {onDismiss !== undefined && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t.dismiss}
          className="shrink-0 rounded p-1 text-warn-soft-fg opacity-70 transition-opacity hover:bg-warn-soft hover:opacity-100"
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * Thông báo thành công tự biến mất — đủ để biết thao tác đã chạy, không cản đường.
 *
 * Là một thẻ có viền chứ không phải một mảng xanh lá tô đặc. Mảng màu đặc là
 * dấu hiệu rõ nhất của một giao diện dựng vội, và ở đây nó còn thừa: cái hình
 * dấu tích đã nói "thành công" rồi, nên phần nền không cần nói lại.
 */
export function Toast({
  message,
  onDone,
}: {
  message: string | null;
  onDone: () => void;
}): React.JSX.Element | null {
  // `onDone` đi qua ref: nơi gọi truyền hàm mũi tên khai tại chỗ, mỗi lượt
  // render của trang là một hàm mới — để nó trong deps thì mỗi phím gõ ở bất kỳ
  // ô nào trên trang đều reset đồng hồ 4 giây và toast không bao giờ tự tắt.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (message === null) return;
    const timer = setTimeout(() => onDoneRef.current(), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  if (message === null) return null;
  return (
    <div className="fixed bottom-5 right-5 z-50 flex max-w-sm items-start gap-2.5 rounded-lg border border-good-edge bg-surface px-4 py-2.5 text-sm text-fg shadow-lg">
      <span className="mt-0.5 shrink-0 text-good" aria-hidden>
        <Icon name="check-circle" size={16} />
      </span>
      <span className="min-w-0">{message}</span>
    </div>
  );
}

// ── Bảng ────────────────────────────────────────────────────────────────────

/**
 * Bảng tràn hết bề ngang khối chứa nó.
 *
 * `-mx-4` ăn đúng vào `p-4` của `Card` và của `Disclosure`, để đường kẻ của
 * bảng chạy sát mép thẻ thay vì dừng lại cách mép một khoảng trắng. Nó là một
 * con số ĐI THEO CẶP: đổi khoảng đệm của `Card` mà quên chỗ này thì mọi bảng
 * trong panel thò ra ngoài hoặc thụt vào trong.
 */
export function Table({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="-mx-4 overflow-x-auto">
      <table className="w-full min-w-[42rem] text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  className,
  onClick,
  sort,
  hint,
}: {
  /** Cột chỉ chứa nút thao tác không cần tiêu đề — bỏ trống thay vì đặt chữ vô nghĩa. */
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  /**
   * Bảng đang xếp theo cột này chưa, và theo chiều nào.
   *
   * `undefined` = cột không tham gia xếp; `null` = xếp được nhưng bảng đang xếp
   * theo cột khác. Phân biệt hai cái đó là cả điểm của prop này: trước đây một
   * tiêu đề bấm được trông y hệt một tiêu đề trơ, và bảng đã xếp trông y hệt
   * bảng chưa xếp — người dùng bấm rồi không biết mình vừa làm gì.
   */
  sort?: 'asc' | 'desc' | null;
  /**
   * Giải thích cột, hiện khi rê chuột.
   *
   * Tiêu đề cột buộc phải ngắn ("Chuyên môn", "Xã hội"), và ngắn tới mức đó thì
   * người đọc chỉ đoán được nghĩa. Tooltip là chỗ duy nhất nói rõ mà không phá
   * bố cục bảng — nhưng nó KHÔNG thay cho phần giải thích viết ra ngoài bảng:
   * chuột không rê được trên điện thoại.
   */
  hint?: string;
}): React.JSX.Element {
  const sortable = onClick !== undefined;
  const active = sort === 'asc' || sort === 'desc';

  return (
    <th
      onClick={onClick}
      title={hint}
      aria-sort={active ? (sort === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cx(
        'border-b border-border bg-surface-2/40 px-4 py-2 text-left text-[0.68rem] font-medium uppercase tracking-label',
        active ? 'text-accent' : 'text-fg-subtle',
        sortable && 'cursor-pointer select-none hover:text-fg',
        hint !== undefined && !sortable && 'cursor-help',
        className,
      )}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {/*
          Mũi tên mờ trên MỌI cột xếp được, không chỉ cột đang xếp: nó là thứ duy
          nhất nói cho người dùng biết tiêu đề này bấm được. Chỉ hiện ở cột đang
          xếp thì các cột kia vẫn là bí mật cho tới khi ai đó bấm thử.
        */}
        {sortable && (
          <span className={active ? '' : 'text-fg-subtle/50'} aria-hidden="true">
            <Icon
              name={sort === 'asc' ? 'arrow-up' : sort === 'desc' ? 'arrow-down' : 'arrows-down-up'}
              size={11}
            />
          </span>
        )}
        {hint !== undefined && (
          <span className="text-fg-subtle/70" aria-hidden="true">
            <Icon name="info" size={11} />
          </span>
        )}
      </span>
    </th>
  );
}

export function Td({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Chú thích hiện khi rê chuột — dành cho ô mà con số hiển thị là kết quả GỘP
   * từ nhiều nguồn: người đọc phải tra được nó gộp từ đâu ngay tại chỗ, chứ
   * không phải mở tài liệu ra mới hiểu vì sao nó khác con số mình tự nhẩm.
   */
  title?: string;
}): React.JSX.Element {
  return (
    <td className={cx('border-b border-border/60 px-4 py-2 text-fg', className)} title={title}>
      {children}
    </td>
  );
}

/**
 * Trước/Sau + tóm tắt "X–Y / Z" cho một bảng phân trang phía server.
 *
 * Dumb — không tự dịch chữ, giống `Table`/`Th`/`Td`: mỗi trang tự giữ dict của
 * mình và truyền chuỗi đã dịch xuống. Đây là chỗ DUY NHẤT dựng UI phân trang —
 * trước lượt thêm này bốn trang (`Members`, `Leaderboard`, `TicketReviews`,
 * `TicketHistory`) mỗi trang tự viết lại đúng khối markup này, còn tám bảng
 * khác không có gì cả (xem `paginate()` ở `BotManager/server/lib/paginate.ts`).
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  summary,
  prevLabel,
  nextLabel,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  summary: string;
  prevLabel: string;
  nextLabel: string;
}): React.JSX.Element {
  return (
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="text-fg-subtle">{summary}</span>
      <div className="flex gap-2">
        <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <Icon name="arrow-left" size={13} />
          {prevLabel}
        </Button>
        <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          {nextLabel}
          <Icon name="arrow-right" size={13} />
        </Button>
      </div>
    </div>
  );
}

/**
 * Hộp xác nhận cho thao tác không hoàn tác được.
 *
 * `pending` và `error` nhận thẳng từ mutation đứng sau nút: đang chạy thì cả
 * hai nút khoá lại (bấm thêm lần nữa là bắn thêm một request thật), còn thất
 * bại thì lỗi phải hiện TRONG hộp — mọi ErrorBox ngoài trang đều nằm dưới lớp
 * phủ của chính hộp này, người dùng không bao giờ thấy.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = false,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element | null {
  const t = useT(UI_TEXT);

  useEffect(() => {
    if (!open || pending) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, pending, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget && !pending) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-5 shadow-lg">
        <h3 className="text-lg font-semibold tracking-heading text-fg">{title}</h3>
        <div className="mt-2 text-sm leading-relaxed text-fg-muted">{body}</div>
        {error !== undefined && error !== null && (
          <div className="mt-3">
            <ErrorBox error={error} />
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={pending}>
            {t.cancel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={pending}>
            {confirmLabel ?? t.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
